import { Box, GlobalStyles, ScopedCssBaseline, ThemeProvider, useTheme } from '@mui/material';
import { capitalMuiDarkTheme, capitalMuiTheme } from '@opengov/capital-mui-theme';
import { LoggerProvider } from '@opengov/psc-react-logger';
import React, { useEffect } from 'react';

import { SwitchMenuProvider } from '../Components/SwitchMenu/SwitchMenuContext';
import { ContextSwitcherDialogControllerProvider } from '../ContextState/useContextSwitcherDialogController';
import { ResponsiveControllerProvider } from '../ContextState/useResponsiveController';
import { EntityProvider } from '../OgAssist/Context/EntityContext';
import { ToastContextProvider } from '../ToastProvider/ToastStackProvider';
import { UtilityTrayProvider } from '../UtilityTray/Context/UtilityTrayContext';

import { CONTAINER_BOX_SHADOW } from '../Components/styleConstants';
import { IAuth0Config, INavBarOptions } from '../types';

import { isEmpty, isUndefined } from 'lodash-es';
import { Auth0ContextWrapper } from '../Components/Auth0ContextWrapper';
import { NavAuthProvider } from '../ContextSwitcher/Context/NavAuthProvider';
import { MenuContextWrapper } from './MenuContextWrapper';
import { NavBarPropsProvider } from './NavBarPropsContext';
import { ResponsiveNavBar } from './ResponsiveNavBar';
import { buildLoggerSessionContext } from './loggerSessionContext';

export const NavBarWithContext: React.FunctionComponent<INavBarOptions> = (props) => {
  const outerTheme = useTheme();
  const {
    appName,
    customHeaderErrorMessage,
    emptyFavoritesHeader,
    emptyFavoritesMessage,
    menuOptions,
    favorites,
    switchMenuOptions,
    getSubmenuItems,
    onFavoriteDeleteClick,
    autoDismissToasts,
    toastAnchorElement,
    onFavoriteReorder,
    onFavoriteSaveClick,
    contextSwitcherOptions,
    utilityTrayOptions,
    hideOpenGovLogo,
    openGovLogoLinkComponent,
    openGovLogoUrl,
    openGovLogoOnClick,
    showOfflineIndicator = false,
    loggerOutput = 'remote',
    unstable_ogAgentOptions
  }: INavBarOptions = props;
  useEffect(() => {
    // insert a meta tag into the head of the document with the app version
    if (typeof document !== 'undefined') {
      const meta = document.createElement('meta');
      meta.name = '__OPENGOV_NAVBAR_VERSION__';
      // vite config global variable is injected here
      // during the build process
      meta.content = __APP_VERSION__;
      document.head.appendChild(meta);
      // add a session storage item to the browser to store the app version
      sessionStorage.setItem('__OPENGOV_NAVBAR_VERSION__', __APP_VERSION__);
    }
  }, []);

  // Combine the Auth0 configuration from the context switcher and the Og Agent
  // These will be consolidated into a single props Auth0 config in the future
  const contextSwitcherAuth0Config = contextSwitcherOptions?.auth0Config;
  const ogAgentAuth0Config = unstable_ogAgentOptions?.auth0Config;

  const auth0Config: IAuth0Config = {
    auth0domain: contextSwitcherAuth0Config?.auth0domain ?? ogAgentAuth0Config?.auth0domain,
    auth0clientID: contextSwitcherAuth0Config?.auth0clientID ?? ogAgentAuth0Config?.auth0clientID,
    auth0callbackUrl: contextSwitcherAuth0Config?.auth0callbackUrl ?? ogAgentAuth0Config?.auth0callbackUrl,
    auth0token: contextSwitcherAuth0Config?.auth0token ?? ogAgentAuth0Config?.auth0token,
    auth0GetAccessToken: contextSwitcherAuth0Config?.auth0GetAccessToken ?? ogAgentAuth0Config?.auth0GetAccessToken,
    auth0skipRedirectCallback:
      contextSwitcherAuth0Config?.auth0skipRedirectCallback ?? ogAgentAuth0Config?.auth0skipRedirectCallback,
    auth0audience: contextSwitcherAuth0Config?.auth0audience ?? ogAgentAuth0Config?.auth0audience,
    onRedirectCallback: contextSwitcherAuth0Config?.onRedirectCallback ?? ogAgentAuth0Config?.onRedirectCallback,
    onAuth0SessionExpired:
      contextSwitcherAuth0Config?.onAuth0SessionExpired ?? ogAgentAuth0Config?.onAuth0SessionExpired
  };
  // End of Selection

  const navBarContainerRef = React.useRef<HTMLDivElement>(null);
  // Determine if OG Assist button should be shown
  // If showOgAssist is explicitly set in unstable_ogAgentOptions, use that value
  // Otherwise, default to true if unstable_ogAgentOptions is provided and not empty
  const showOgAssist =
    unstable_ogAgentOptions?.showOgAssist !== undefined
      ? unstable_ogAgentOptions.showOgAssist
      : !isUndefined(unstable_ogAgentOptions) && !isEmpty(unstable_ogAgentOptions);

  // Session context is applied to all log events sent from components-nav-bar and lands in
  // the log store as session attributes. It is built from an allowlist so that credentials
  // such as `auth0Config.auth0token` never leave the browser; see `loggerSessionContext.ts`.
  const sessionContext =
    loggerOutput !== 'none'
      ? buildLoggerSessionContext({ appName, menuOptions, contextSwitcherOptions, utilityTrayOptions })
      : {};
  return (
    <LoggerProvider
      sessionContext={sessionContext}
      config={{
        app: {
          name: 'components-nav-bar',
          version: __APP_VERSION__,
          environment: process.env.NODE_ENV
        },
        broadcastJsLoggerEvents: true,
        loggerOutput,
        offlineConfig: {
          maxRetries: 3,
          retryDelay: 5000
        }
      }}
    >
      <ThemeProvider theme={outerTheme.palette.mode === 'dark' ? capitalMuiDarkTheme : capitalMuiTheme}>
        <GlobalStyles
          styles={{
            ':root': {
              // Exposing --nav-bar-top-padding for teams to have control
              // over top padding on mobile devices
              // https://opengovinc.atlassian.net/browse/PSS-1797
              // Defaulting to env(safe-area-inset-top) as the widely supported fallback
              // https://developer.mozilla.org/en-US/docs/Web/CSS/env
              '--nav-bar-top-padding': 'env(safe-area-inset-top)'
            }
          }}
        />
        <ScopedCssBaseline>
          <NavBarPropsProvider {...props}>
            <ToastContextProvider autoDismissToasts={autoDismissToasts} toastAnchorElement={toastAnchorElement}>
              <MenuContextWrapper
                menuOptions={menuOptions}
                favorites={favorites}
                getSubmenuItems={getSubmenuItems}
                onFavoriteDeleteClick={onFavoriteDeleteClick}
                onFavoriteReorder={onFavoriteReorder}
                onFavoriteSaveClick={onFavoriteSaveClick}
                hideOpenGovLogo={hideOpenGovLogo}
                openGovLogoUrl={openGovLogoUrl}
                openGovLogoLinkComponent={openGovLogoLinkComponent}
                openGovLogoOnClick={openGovLogoOnClick}
              >
                <SwitchMenuProvider value={switchMenuOptions}>
                  <Auth0ContextWrapper auth0Config={auth0Config}>
                    <NavAuthProvider auth0Config={auth0Config}>
                      <UtilityTrayProvider value={{ ...utilityTrayOptions, showOgAssist }}>
                        <EntityProvider>
                          <ContextSwitcherDialogControllerProvider>
                            <ResponsiveControllerProvider navBarContainerRef={navBarContainerRef}>
                              <Box
                                ref={navBarContainerRef}
                                display="flex"
                                width="100%"
                                zIndex={999}
                                position="relative"
                                sx={{
                                  pt: 'var(--nav-bar-top-padding)',
                                  backgroundColor: 'background.paper'
                                }}
                                boxShadow={CONTAINER_BOX_SHADOW}
                              >
                                <ResponsiveNavBar
                                  appName={appName}
                                  contextSwitcherOptions={contextSwitcherOptions}
                                  showOfflineIndicator={showOfflineIndicator}
                                  menuOptions={menuOptions}
                                  switchMenuOptions={switchMenuOptions}
                                  favoritesOptions={{
                                    favorites,
                                    customHeaderErrorMessage,
                                    emptyFavoritesHeader,
                                    emptyFavoritesMessage
                                  }}
                                />
                              </Box>
                            </ResponsiveControllerProvider>
                          </ContextSwitcherDialogControllerProvider>
                        </EntityProvider>
                      </UtilityTrayProvider>
                    </NavAuthProvider>
                  </Auth0ContextWrapper>
                </SwitchMenuProvider>
              </MenuContextWrapper>
            </ToastContextProvider>
          </NavBarPropsProvider>
        </ScopedCssBaseline>
      </ThemeProvider>
    </LoggerProvider>
  );
};
